import React, { useEffect, useState, useRef, useCallback } from "react";
import api from "../api";
import { Input } from "./ui/input";
import { MapPin, Plus, X, Search } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Multi-establishment selector with Nominatim-style autocomplete.
 * - Searches the existing pools collection by name
 * - Lets the user pick multiple establishments
 * - If nothing matches, offers a link to add a new pool manually
 *
 * Props:
 *  - value: array of pool_id strings
 *  - onChange: (next: string[]) => void
 *  - testId
 */
export default function PoolMultiSelect({ value = [], onChange, testId = "pool-multi-select" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [pools, setPools] = useState([]);  // all pools cached
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get("/pools").then((r) => {
      setPools(r.data || []);
    }).finally(() => setLoading(false));
  }, []);

  // Outside-click to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const search = useCallback((text) => {
    const v = (text || "").trim().toLowerCase();
    if (!v) { setResults([]); return; }
    const r = pools
      .filter((p) => !value.includes(p.pool_id))
      .filter((p) =>
        (p.name || "").toLowerCase().includes(v) ||
        (p.address || "").toLowerCase().includes(v))
      .slice(0, 10);
    setResults(r);
  }, [pools, value]);

  const onChangeQuery = (text) => {
    setQ(text);
    search(text);
    setOpen(true);
  };

  const add = (pool) => {
    if (!value.includes(pool.pool_id)) onChange([...value, pool.pool_id]);
    setQ(""); setResults([]); setOpen(false);
  };
  const remove = (id) => onChange(value.filter((x) => x !== id));

  const selectedPools = value
    .map((id) => pools.find((p) => p.pool_id === id))
    .filter(Boolean);

  return (
    <div ref={ref} className="space-y-2" data-testid={testId}>
      <div className="relative">
        <div className="flex items-center gap-2 border border-slate-200 rounded-xl bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-sky-200">
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <Input
            value={q}
            onChange={(e) => onChangeQuery(e.target.value)}
            onFocus={() => { if (q) setOpen(true); }}
            placeholder="Rechercher une piscine, centre aquatique, club…"
            className="border-0 focus-visible:ring-0 px-0 h-7"
            data-testid={`${testId}-input`}
          />
        </div>
        {open && (results.length > 0 || q) && (
          <div className="absolute left-0 right-0 mt-1 bg-white border rounded-xl shadow-lg max-h-72 overflow-auto z-[1500]">
            {loading && <div className="p-3 text-xs text-slate-500">Chargement…</div>}
            {results.map((p, i) => (
              <button
                type="button"
                key={p.pool_id}
                onClick={() => add(p)}
                className="w-full text-left px-3 py-2 hover:bg-sky-50 hover:text-sky-700 border-b last:border-0 border-slate-100 text-sm"
                data-testid={`${testId}-result-${i}`}
              >
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-sky-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.address}</div>
                  </div>
                </div>
              </button>
            ))}
            {!loading && q && results.length === 0 && (
              <div className="p-3 text-sm text-slate-600">
                <div className="mb-2">Aucun résultat pour "<strong>{q}</strong>"</div>
                <Link
                  to="/map?add=1"
                  className="inline-flex items-center gap-1 text-sky-700 font-medium hover:underline"
                  data-testid={`${testId}-add-manual`}
                >
                  <Plus className="w-4 h-4" />Ajouter cet établissement manuellement
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
      {selectedPools.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`${testId}-selected`}>
          {selectedPools.map((p) => (
            <span
              key={p.pool_id}
              className="inline-flex items-center gap-1 bg-sky-100 text-sky-800 rounded-full pl-3 pr-1 py-1 text-xs"
              data-testid={`${testId}-chip-${p.pool_id}`}
            >
              <MapPin className="w-3 h-3" />{p.name}
              <button
                type="button"
                onClick={() => remove(p.pool_id)}
                className="ml-1 rounded-full hover:bg-sky-200 p-0.5"
                aria-label={`Retirer ${p.name}`}
                data-testid={`${testId}-remove-${p.pool_id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
